import React from 'react';

import { CrossCircleIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import type { ButtonWithPermissionsProps } from '@vlabs/monokit/shared/ui/button-with-permissions';
import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';

import { ClearLists } from './ClearLists';

const displayName = 'ClearListsButton';
const testid = buildTestid(displayName);

interface TPermissions {
  method?: 'allof' | 'oneof';
  rules: string[];
}

interface ClearListsButtonProps extends
  WithDataTestid,
  Omit<ButtonWithPermissionsProps, 'onClick' | 'permissions'> {
  listIds: string[];
  onClearLists?: VoidFunction;
  permissions?: TPermissions;
}

function ClearListsButton({
  listIds,
  onClearLists = undefined,
  permissions = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: ClearListsButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });

  return (
    <React.Fragment>
      {modal.wrap(
        <ClearLists
          listIds={listIds}
          onCancel={modal.close}
          onClearLists={onClearLists}
        />,
      )}

      <ButtonWithPermissions
        icon={<CrossCircleIcon />}
        onClick={modal.open}
        permissions={permissions}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {t('features:clear_lists.btn.clear')}
      </ButtonWithPermissions>
    </React.Fragment>
  );
}

export { ClearListsButton };
